import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { NotificationsService } from '../services/notifications.service';
import type { NotificationItem } from '../types/notifications.types';
import { useEffect } from 'react';
import { supabase } from '@/core/database/supabase';

export function useNotifications(userId?: string, _options?: unknown) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['notifications', userId],
    queryFn: () => NotificationsService.getNotifications(userId!),
    enabled: !!userId,
  });

  const markAsReadMutation = useMutation({
    mutationFn: (id: string) => NotificationsService.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', userId] });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: () => NotificationsService.markAllAsRead(userId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', userId] });
    },
  });

  // Realtime subscription
  useEffect(() => {
    if (!userId) return;

    const channel = NotificationsService.subscribe(userId, (payload) => {
      // Tự động invalidate query khi có thông báo mới
      queryClient.invalidateQueries({ queryKey: ['notifications', userId] });
      
      // Có thể thêm logic hiển thị browser notification hoặc toast ở đây
    });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, queryClient]);

  return {
    notifications: query.data || [],
    unreadCount: (query.data || []).filter(n => n.status_code === 'unread').length,
    isLoading: query.isLoading,
    markAsRead: markAsReadMutation.mutateAsync,
    markAllAsRead: markAllAsReadMutation.mutateAsync,
  };
}
